import { useState, useEffect, useCallback } from "react";
import { purchaseListService } from "@/services";
import {
  PurchaseListState,
  PurchaseListFlowState,
  UsePurchaseListReturn,
  PurchaseListRequest,
  AssignmentRequest,
  QRGenerationRequest,
} from "@/types/purchaseList.types";
import { useAuth } from "./useAuth";

const initialState: PurchaseListState = {
  purchaseLists: [],
  assignments: [],
  availableProducts: [],
  currentPurchaseList: null,
  error: null,
  flowState: "idle",
};

const initialFlowState: PurchaseListFlowState = "idle";

export const usePurchaseList = (): UsePurchaseListReturn => {
  const [state, setState] = useState<PurchaseListState>(initialState);
  const [_flowState, _setFlowState] =
    useState<PurchaseListFlowState>(initialFlowState);
  const { user } = useAuth();

  // Cargar listas de compra del apoderado
  const loadPurchaseLists = useCallback(
    async (): Promise<boolean> => {
      if (!user || !user.roles?.includes("apoderado")) return false;

      setState((prev) => ({ ...prev, error: null }));

      try {
        const response = await purchaseListService.getPurchaseListsByApoderado(
          
        );
        setState((prev) => ({
          ...prev,
          purchaseLists: response || [],
        }));
        return true;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Error cargando listas de compra",
        }));
        return false;
      }
    },
    [user]
  );

  // Cargar asignaciones
  const loadAssignments = useCallback(
    async (studentId: string): Promise<boolean> => {
      if (!user || !user.roles?.includes("apoderado")) return false;

      setState((prev) => ({ ...prev, error: null }));

      try {
        const response = await purchaseListService.getStudentAssignments(
          studentId
        );
        setState((prev) => ({
          ...prev,
          assignments: response || [],
        }));
        return true;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Error cargando asignaciones",
        }));
        return false;
      }
    },
    [user]
  );

  // Crear lista de compra
  const createPurchaseList = useCallback(
    async (request: PurchaseListRequest) => {
      if (!user || !user.roles?.includes("apoderado")) {
        throw new Error("Solo los apoderados pueden crear listas de compra");
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await purchaseListService.createPurchaseList(request);
        setState((prev) => ({
          ...prev,
          purchaseLists: [...prev.purchaseLists, response],
          loading: false,
        }));
        return true;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Error creando lista de compra",
        }));
        return false;
      }
    },
    [user]
  );

  // Asignar productos a estudiante
  const assignProductsToStudent = useCallback(
    async (purchaseListId: string, request: AssignmentRequest) => {
      if (!user || !user.roles?.includes("apoderado")) {
        throw new Error("Solo los apoderados pueden asignar productos");
      }

      setState((prev) => ({ ...prev, error: null }));

      try {
        const response = await purchaseListService.assignProductsToStudent(
          purchaseListId,
          request
        );
        setState((prev) => ({
          ...prev,
          assignments: [...prev.assignments, response],
        }));
        return true;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Error asignando productos",
        }));
        return false;
      }
    },
    [user]
  );

  // Generar QR para lista de compra
  const generateQR = useCallback(
    async (request: QRGenerationRequest) => {
      if (!user || !user.roles?.includes("apoderado")) {
        throw new Error("Solo los apoderados pueden generar códigos QR");
      }

      setState((prev) => ({ ...prev, error: null }));

      try {
        await purchaseListService.generateQR("temp-id", request);
        setState((prev) => ({
          ...prev,
        }));
        return true;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Error generando código QR",
        }));
        return false;
      }
    },
    [user]
  );

  // Obtener productos disponibles para estudiante
  const getStudentAvailableProducts = useCallback(async (studentId: string) => {
    setState((prev) => ({ ...prev, error: null }));

    try {
      const response = await purchaseListService.getStudentAvailableProducts(
        studentId
      );
      setState((prev) => ({
        ...prev,
        availableProducts: response.availableProducts || [],
      }));
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Error cargando productos disponibles",
      }));
      return false;
    }
  }, []);

  // Obtener lista de compra por ID
  const getPurchaseListById = useCallback(async (purchaseListId: string) => {
    setState((prev) => ({ ...prev, error: null }));

    try {
      await purchaseListService.getPurchaseListById(purchaseListId);
      setState((prev) => ({
        ...prev,
      }));
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Error cargando lista de compra",
      }));
      return false;
    }
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (user && user.roles?.includes("apoderado")) {
      loadPurchaseLists();
      loadAssignments(user.id);
    }
  }, [user, loadPurchaseLists, loadAssignments]);

  return {
    // Estado
    state,
    isLoading: false,
    isLoadingAssignments: false,
    isLoadingProducts: false,

    // Acciones
    createPurchaseList,
    assignProductsToStudent,
    generateQR,
    getStudentAvailableProducts,
    getPurchaseListById,
    getPurchaseListsByApoderado: loadPurchaseLists,
    getStudentAssignments: loadAssignments,

    // Utilidades
    resetState: () => setState(initialState),
    clearError,

    // Estados de error
    error: state.error,
  };
};
